import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, Wallet, LineChart, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { properties, developers, testimonials } from "@/data/mock";
import PropertyCard from "@/components/PropertyCard";

const Home = () => {
  const { t } = useApp();
  const featured = properties.filter((p) => p.hot).slice(0, 3);

  return (
    <main data-testid="home-page" className="bg-[#0A0E1A] text-[#F5F0E8] pt-20">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative grain min-h-[90vh] flex items-center overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/1381722/pexels-photo-1381722.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920"
            alt="Dubai skyline at golden hour"
            className="w-full h-full object-cover slow-zoom"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0E1A]/95 via-[#0A0E1A]/70 to-[#0A0E1A]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E1A] via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 w-full">
          <div className="max-w-3xl reveal">
            <p className="overline mb-6">{t("hero_overline")}</p>
            <h1
              data-testid="hero-title"
              className="font-display text-4xl sm:text-5xl lg:text-7xl leading-[1.05] font-medium"
            >
              {t("hero_title")}
            </h1>
            <p className="mt-7 text-lg sm:text-xl text-[#F5F0E8]/85 font-light max-w-xl leading-relaxed">
              {t("hero_sub")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/properties"
                data-testid="hero-cta-primary"
                className="btn-gold inline-flex items-center gap-3 px-8 py-4 text-xs tracking-[0.22em] uppercase font-medium"
              >
                {t("hero_cta_primary")} <ArrowRight className="w-4 h-4 ltr-flip" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-secondary"
                className="btn-ghost-gold inline-flex items-center gap-3 px-8 py-4 text-xs tracking-[0.22em] uppercase"
              >
                {t("hero_cta_secondary")}
              </Link>
            </div>
          </div>
        </div>

        {/* Trust bar */}
        <div className="absolute bottom-0 inset-x-0 border-t border-[#C9A84C]/20 bg-[#0A0E1A]/85 backdrop-blur-sm">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-5 grid grid-cols-2 lg:grid-cols-4 gap-4 text-center">
            {[
              t("trust_licensed"),
              t("trust_experience"),
              t("trust_clients"),
              t("trust_volume"),
            ].map((label, i) => (
              <div key={i} className="text-xs sm:text-sm tracking-wider text-[#F5F0E8]/85 font-light">
                <span className="text-[#C9A84C]">·</span> {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DEVELOPERS */}
      <section data-testid="developers-section" className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-14">
            <p className="overline">{t("sec_developers_overline")}</p>
            <h2 className="font-display text-4xl lg:text-5xl mt-4 text-[#F5F0E8]">
              {t("sec_developers_title")}
            </h2>
            <p className="mt-5 text-[#A0A5B5] font-light leading-relaxed">
              {t("sec_developers_sub")}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-[#C9A84C]/15">
            {developers.map((dev) => (
              <div
                key={dev.id}
                data-testid={`developer-tile-${dev.id}`}
                className="bg-[#0A0E1A] p-8 lg:p-12 text-center group hover:bg-[#1E2235] transition-colors duration-500"
              >
                <p className="font-display text-2xl lg:text-3xl text-[#F5F0E8] group-hover:text-[#C9A84C] transition-colors">
                  {dev.name}
                </p>
                <p className="mt-2 text-[10px] tracking-[0.22em] uppercase text-[#A0A5B5]">
                  {dev.tagline}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY OFF-PLAN */}
      <section data-testid="why-offplan-section" className="py-24 lg:py-32 border-t border-[#C9A84C]/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-16">
            <p className="overline">{t("sec_why_overline")}</p>
            <h2 className="font-display text-4xl lg:text-6xl mt-4 leading-[1.1] text-[#F5F0E8]">
              {t("sec_why_title")}
            </h2>
          </div>
          <div className="grid lg:grid-cols-3 gap-px bg-[#C9A84C]/15">
            {[
              { Icon: TrendingUp, title: t("sec_why_1_title"), body: t("sec_why_1_body"), n: "01" },
              { Icon: Wallet, title: t("sec_why_2_title"), body: t("sec_why_2_body"), n: "02" },
              { Icon: LineChart, title: t("sec_why_3_title"), body: t("sec_why_3_body"), n: "03" },
            ].map(({ Icon, title, body, n }, i) => (
              <div
                key={i}
                data-testid={`why-card-${i}`}
                className="bg-[#0A0E1A] p-10 lg:p-14 hover:bg-[#1E2235]/50 transition-all duration-500"
              >
                <div className="flex items-start justify-between mb-8">
                  <Icon className="w-8 h-8 text-[#C9A84C]" strokeWidth={1.2} />
                  <span className="font-display italic text-[#C9A84C]/50 text-2xl">{n}</span>
                </div>
                <h3 className="font-display text-2xl lg:text-3xl text-[#F5F0E8] leading-tight">
                  {title}
                </h3>
                <div className="gold-divider my-5" />
                <p className="text-[#A0A5B5] font-light leading-relaxed text-sm">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="py-24 lg:py-32 border-t border-[#C9A84C]/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-16">
            <p className="overline">{t("sec_testimonials_overline")}</p>
            <h2 className="font-display text-4xl lg:text-5xl mt-4 text-[#F5F0E8]">
              {t("sec_testimonials_title")}
            </h2>
          </div>
          <div className="overflow-hidden">
            <div className="flex gap-8 marquee-track" style={{ width: "200%" }}>
              {[...testimonials, ...testimonials].map((tm, idx) => (
                <figure
                  key={idx}
                  data-testid={`testimonial-${idx}`}
                  className="flex-shrink-0 w-[360px] lg:w-[460px] border border-[#C9A84C]/15 bg-[#1E2235]/30 p-10"
                >
                  <div className="flex gap-1 text-[#C9A84C] mb-5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-current" />
                    ))}
                  </div>
                  <blockquote className="font-display italic text-xl lg:text-2xl leading-snug text-[#F5F0E8]/90">
                    "{tm.quote}"
                  </blockquote>
                  <div className="gold-divider my-6" />
                  <figcaption>
                    <p className="text-sm text-[#F5F0E8]">{tm.name}</p>
                    <p className="text-xs text-[#A0A5B5] mt-1">{tm.role}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOT PROPERTIES */}
      <section data-testid="hot-properties-section" className="py-24 lg:py-32 border-t border-[#C9A84C]/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <p className="overline">{t("sec_hot_overline")}</p>
              <h2 className="font-display text-4xl lg:text-5xl mt-4 text-[#F5F0E8]">
                {t("sec_hot_title")}
              </h2>
              <p className="mt-5 text-[#A0A5B5] font-light max-w-md">{t("sec_hot_sub")}</p>
            </div>
            <Link
              to="/properties"
              data-testid="view-all-properties-link"
              className="inline-flex items-center gap-3 text-xs tracking-[0.22em] uppercase text-[#C9A84C] hover:text-[#F5F0E8] transition-colors"
            >
              {t("view_all_properties")} <ArrowRight className="w-4 h-4 ltr-flip" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featured.map((p, i) => (
              <PropertyCard key={p.id} property={p} index={i} />
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;